/**
 * How a project list row describes the devices of a project.
 *
 * Deliberately import-free: it is pulled in by the project list rows, and a
 * plain structural parameter keeps it out of the overview model's import chain
 * (see the import-cycle rule in CLAUDE.md).
 */

/** Which of the three device wordings a project list row shows. */
export enum DeviceCountDisplay {
  /** No device was ever assigned: the row shows no device text at all. */
  HIDDEN = "HIDDEN",
  /** Every device the project used is still on site ("3 Geräte aktiv"). */
  ALL_ACTIVE = "ALL_ACTIVE",
  /** Some are still on site, the rest were picked up ("1 von 3 Geräten aktiv"). */
  SOME_ACTIVE = "SOME_ACTIVE",
  /** All devices were picked up again ("3 Geräte"). */
  NONE_ACTIVE = "NONE_ACTIVE",
}

export interface DeviceCountInfo {
  display: DeviceCountDisplay;
  /** Distinct devices still on site. */
  activeDevices: number;
  /** Distinct devices the project used at any point. */
  totalDevices: number;
}

/** The two overview fields the wording depends on. */
export interface ProjectDeviceCounts {
  numDevices?: number;
  numDevicesTotal?: number;
}

/**
 * Works out how a project's devices should be described in a list row.
 *
 * `numDevicesTotal` is absent on every overview entry written before the field
 * existed, because no backfill was run, and it also disappears whenever another
 * IRES app rewrites an entry as a whole. Falling back to the active count then
 * reproduces the previous behaviour exactly: a plain count with "aktiv", or no
 * device text at all. That is also why a total below the active count is
 * treated as fully active rather than rendered as "3 von 2".
 */
export function getDeviceCountInfo(
  projectOverview: ProjectDeviceCounts
): DeviceCountInfo {
  const activeDevices = Math.max(projectOverview.numDevices ?? 0, 0);
  const totalDevices = Math.max(
    projectOverview.numDevicesTotal ?? activeDevices,
    activeDevices
  );

  if (totalDevices === 0) {
    return { display: DeviceCountDisplay.HIDDEN, activeDevices, totalDevices };
  }

  if (activeDevices === 0) {
    return { display: DeviceCountDisplay.NONE_ACTIVE, activeDevices, totalDevices };
  }

  if (activeDevices >= totalDevices) {
    return { display: DeviceCountDisplay.ALL_ACTIVE, activeDevices, totalDevices };
  }

  return { display: DeviceCountDisplay.SOME_ACTIVE, activeDevices, totalDevices };
}

/** The devices tab title on the project detail screen, before translation. */
export interface DeviceTabCount {
  /** At least one device is still on site: the title reads "Aktive Geräte". */
  hasActiveDevices: boolean;
  /** The bracketed figure: "1/3" or "3" while devices are on site, otherwise the total. */
  count: string;
}

/**
 * Works out the devices tab title from the same rule as the list rows: "1/3"
 * while some devices are on site, "3" when all of them are, and the total once
 * none is left (or "0" for a project that never had one).
 */
export function getDeviceTabCount(info: DeviceCountInfo): DeviceTabCount {
  switch (info.display) {
    case DeviceCountDisplay.SOME_ACTIVE:
      return { hasActiveDevices: true, count: `${info.activeDevices}/${info.totalDevices}` };
    case DeviceCountDisplay.ALL_ACTIVE:
      return { hasActiveDevices: true, count: `${info.activeDevices}` };
    default:
      return { hasActiveDevices: false, count: `${info.totalDevices}` };
  }
}
